import { Button, Link, Typography, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'

import { updateKennelAssignment } from '../../actions/kennels/kennels.actions'
import { getVisitsByFacilityId } from '../../actions/visits/visits.actions'
import KennelBlockDialogNew from '../../components/Dialogs/KennelBlockDialogNew'
import { KennelsTable } from '../../components/Tables/KennelsTable'
import { getPetSize } from '../../services/helpers'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1
	},
	bottomBar: {
		position: 'fixed',
		bottom: 0,
		width: '50%',
		height: 60,
		background: '#FFF',
		border: '1px solid #D0CFD2',
		borderLeftWidth: 0,
		borderTopRightRadius: 6,
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'center',
		padding: 20,
		'& i': {
			marginRight: 17,
			cursor: 'pointer',
			color: '#D0CFD2'
		},
		'& .whee': {
			fontFamily: 'Nunito',
			fontWeight: '600',
			fontSize: 16,
			color: '#1A1A35',
			marginRight: 17
		},
		'& .checked': {
			color: '#6B4AD6'
		},
		'& .selectedFacility': {
			marginLeft: 10,
			fontSize: 16,
			color: '#6B4AD6 !important',
			textTransform: 'uppercase',
			fontFamily: 'Nunito',
			fontWeight: 'bold'
		}
	},
	changeButton: {
		padding: 0,
		'&:hover': {
			background: 'transparent'
		}
	},
	label: {
		fontSize: '16px',
		fontWeight: 600,
		lineHeight: '22px',
		letterSpacing: '0px',
		textAlign: 'left',
		textTransform: 'none',
		color: theme.palette.secondary.main
	}
}))

const Kennels = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const theme = useTheme()

	const facility = useSelector((state) => state.specific.facilities.selected)
	const selectedFilters = useSelector((state) => state.specific.facilities.selectedKennelFilters)
	const visits = useSelector((state) => state.specific.visits.visits)
	const pets = useSelector((state) => state.specific.visits.pets)

	const playgroups = (facility && facility.playgroups) || []

	let initialAggregates = {
		boarding_dogs_tonight: {
			value: 0
		},
		standard_kennels: {
			value: 0
		},
		luxury_kennels: {
			value: 0
		},
		['standard-shared_kennels']: {
			value: 0
		},
		['luxury-shared_kennels']: {
			value: 0
		},
		kennel_free_dogs: {
			value: 0
		}
	}

	const [date, setDate] = useState(moment().format('YYYY-MM-DD'))
	const [data, setData] = useState([])
	const [aggregates, setAggregates] = useState(initialAggregates)
	const [isKennelBlockPopupShown, setIsKennelBlockPopupShown] = useState(false)
	const [pet, setPet] = useState(null)

	const getTags = (pet) => {
		// Internal Adjustments', 'External Adjustments', and 'Kennel Tags'
		let tags = []
		if (pet && pet.tags && pet.tags.kennel) {
			pet.tags.kennel.forEach((k, i) => {
				tags.push(k.name)
			})
		}
		if (pet.internal_adjustment) tags.push(pet.internal_adjustment)
		if (pet.external_adjustment) tags.push(pet.external_adjustment)
		return tags.map((t) => {
			return (
				<Typography
					variant="body1"
					style={{
						fontSize: 14,
						color: '#1a1a35',
						fontWeight: '600',
						textTransform: 'capitalize',
						lineHeight: '19px'
					}}>
					{t}
				</Typography>
			)
		})
	}

	const getPlaygroupColumn = (data) => {
		const { playgroup } = data;

		return (
			<Typography
				variant="body1"
				style={{
					fontSize: 14,
					color: '#1a1a35',
					fontWeight: '600',
					textTransform: 'capitalize',
					lineHeight: '19px'
				}}>
				{playgroup || 'Unassigned'}
			</Typography>
		)
	}

	const renderNightProgram = (data) => {
		let { visit } = data
		let kennelName = data.boarding_option ? data.boarding_option.kennel_type : 'Standard'
		if (visit.sharing_kennels) kennelName += ' - Shared'

		let primary =
			data?.household?.accounts && data.household.accounts.length
				? data.household.accounts.find((a) => a.primary)
				: null

		return (
			<div>
				<Typography
					variant="body1"
					style={{
						fontSize: 14,
						color: '#1a1a35',
						fontWeight: '600',
						textTransform: 'capitalize',
						lineHeight: '19px'
					}}>
					{kennelName}
				</Typography>
				{visit.sharing_kennels
					? visit.pets
						.filter((p) => p._id !== data._id)
						.map((pet) => {
							return (
								<Typography
									variant="body1"
									style={{
										fontSize: 14,
										color: '#777679',
										fontWeight: '600',
										textTransform: 'capitalize',
										lineHeight: '19px'
									}}>
									{pet.name} {primary?.last_name || ''}
								</Typography>
							)
						})
					: null}
			</div>
		)
	}

	const getColumns = () => {
		return [
			{
				title: 'Dates',
				render: (data) => renderDates(data),
				customFilterAndSearch: (value, rowData) => {
					let date = getDate(rowData)
					let search = value

					return search.toUpperCase().includes(date.toUpperCase())
				},
				customSort: (a, b) => {
					let aEndDate = moment(a.visit.endDate).unix()
					let bEndDate = moment(b.visit.endDate).unix()

					return aEndDate - bEndDate
				}
			},
			{
				title: 'Pet(s)',
				render: (data) => renderPet(data),
				customFilterAndSearch: (value, rowData) => {
					let name = value
					if (name) name = name.toUpperCase()

					let primary =
						rowData?.household?.accounts && rowData.household.accounts.length
							? rowData.household.accounts.find((a) => a.primary)
							: null

					let petName = `${rowData.name}${primary ? ` ${primary.last_name}` : ''}`

					return petName.toUpperCase().includes(name)
				},
				customSort: (a, b) => {
					let aPrimary =
						a.household.accounts && a.household.accounts.length
							? a.household.accounts.find((account) => account.primary)
							: null
					let bPrimary =
						b.household.accounts && b.household.accounts.length
							? b.household.accounts.find((account) => account.primary)
							: null

					let aPetName = `${a.name}${aPrimary ? ` ${aPrimary.last_name}` : ''}`

					let bPetName = `${b.name}${bPrimary ? ` ${bPrimary.last_name}` : ''}`

					return aPetName.localeCompare(bPetName)
				}
			},
			{
				title: 'Night Program',
				render: (data) => renderNightProgram(data),
				customFilterAndSearch: (value, rowData) => {
					let nightProgram = getNightProgram(rowData)
					let search = value

					return nightProgram.includes(search)
				},
				customSort: (a, b) => {
					let aNightProgram = getNightProgram(a)
					let bNightProgram = getNightProgram(b)

					return aNightProgram.localeCompare(bNightProgram)
				}
			},
			{
				title: 'Tags & Adjustments',
				render: (data) => getTags(data)
			},
			{
				title: 'Playgroup',
				render: (data) => getPlaygroupColumn(data),
				width: 142,
				customFilterAndSearch: (value, rowData) => {
					const playgroup = rowData?.playgroup?.toUpperCase() || ''
					const search = value.toUpperCase()

					return playgroup.includes(search)
				},
				customSort: (a, b) => {
					const aPlayroup = a.playgroup
					const bPlaygroup = b.playgroup

					return aPlayroup?.localeCompare(bPlaygroup)
				}
			},
			{
				title: 'Kennel',
				render: (data) => {
					let nightKennelInfo = getKennelInfo(data)

					return (
						<div style={{ display: 'flex' }}>
							{nightKennelInfo ? (
								<Typography variant="body1" style={{ marginRight: 8 }}>
									{nightKennelInfo}
								</Typography>
							) : null}
							<Button
								disableRipple
								variant="text"
								classes={{
									root: classes.changeButton,
									label: classes.label
								}}
								onClick={() => {
									setPet(data)
									setIsKennelBlockPopupShown(true)
								}}>
								{nightKennelInfo ? 'Change' : 'Assign'}
							</Button>
						</div>
					)
				},
				customFilterAndSearch: (value, rowData) => {
					let nightKennelInfo = getKennelInfo(rowData)
					let search = value

					return nightKennelInfo.includes(search)
				},
				customSort: (a, b) => {
					let aNightKennelInfo = getKennelInfo(a)
					let bNightKennelInfo = getKennelInfo(b)

					return aNightKennelInfo.localeCompare(bNightKennelInfo)
				}
			}
		]
	}

	const getDate = (data) => {
		return `${moment(data.visit.startDate).format('MMM DD')} - ${moment(data.visit.endDate).format(
			'MMM DD, YYYY'
		)}`
	}

	const renderDates = (data) => <Typography variant="body2">{getDate(data)}</Typography>

	const renderPet = (data) => {
		let primary =
			data?.household?.accounts && data.household.accounts.length
				? data.household.accounts.find((a) => a.primary)
				: null

		let location = getLocation(data)

		return (
			<div
				style={{
					display: 'flex',
					flexDirection: 'column'
				}}>
				<Link
					component={RouterLink}
					style={{
						color: theme.palette.secondary.main,
						marginBottom: 5,
						fontSize: '16px',
						lineHeight: '22px'
					}}
					onClick={(e) => e.stopPropagation()}
					to={`/clients/${data.visit.household._id}/pets/${data._id}`}>
					{`${data.name}${primary ? ` ${primary.last_name}` : ''}`}
				</Link>
				<Typography variant="caption" style={{ color: '#777679' }}>
					{location}
				</Typography>
			</div>
		)
	}

	const getPlaygroup = (pet) => {
		const playgroup = pet?.status?.playgroup && playgroups.find((p) => pet.status.playgroup === p._id)

		return playgroup?.name || null
	}

	const getLocation = (pet) => {
		const { checked_in, checked_out, location, playgroup_id = '' } = pet?.status
		const { areas, kennels, playgroups } = facility
		let text = ""
		if (!checked_in || checked_out) {
			text = "Offsite"
		} else {
			if (location === "resting") {
				text = 'Resting'
				let kennel = kennels.find(k =>
					k?.pet_ids?.includes(pet?._id)
				)
				if (kennel) {
					const area = areas.find(a => a?._id === kennel?.area_id)
					const text_area = `${area ? `${area.name} - ` : ""}${kennel.reference_number}`
					text = text + ': ' + text_area
				}
			} else if (location === "playing") {
				text = 'Playing'
				let playgroup = playgroups.find(
					g => g?._id === playgroup_id
				)
				if (playgroup) {
					let area = areas.find(a => a?._id === playgroup?.area_id)
					const text_area = `${area?.name || ''}`
					text = text + ': ' + text_area
				}
			} else {
				if (!pet.location) {
					text = "Onsite"
				} else {
					text = location
				}
			}
		}
		return text
	}

	const getNightProgram = (data) => {
		let { visit } = data

		return `${data.boarding_option ? data.boarding_option.kennel_type : 'Standard'}${visit.sharing_kennels ? '-Shared' : ''
			}`
	}

	const getKennelInfo = (data) => {
		let { visit } = data

		if (facility && visit) {
			let { kennels } = facility

			let kennel = kennels.find((k) => {
				return k?.pet_ids?.includes(data._id)
			})

			let area

			if (kennel && kennel.area_id) area = facility.areas.find((a) => a._id === kennel.area_id)

			return `${area ? `${area.abbreviation ? `Rm ${area.abbreviation}` : area.name}: ` : ''}${kennel && kennel.reference_number ? kennel.reference_number : ''
				}`
		}

		return null
	}

	useEffect(() => {
		dispatch({ type: 'CLEAR_VISITS' })
	}, [])

	useEffect(() => {
		if (facility) {
			dispatch(getVisitsByFacilityId(facility, date))
		}
	}, [date, facility])

	useEffect(() => {
		if (pets) {
			let filteredData = visits
				.filter((v) => v.status && v.status.id !== 7)
				.filter(
					(visit) =>
						visit.type === 'boarding' &&
						moment(visit.endDate).startOf('d').isAfter(moment(date).startOf('d'))
				)
				.map((visit) =>
					visit.pets.map((p) => ({
						...p,
						visit,
						playgroup: getPlaygroup(p),
						household: p.household || visit.household || { accounts: [{ primary: true }] },
						boarding_option:
							visit.boarding_options && visit.boarding_options.length
								? visit.boarding_options.find((o) => o.pet_id === p._id)
								: null
					}))
				)
				.flat(1)

			if (Object.keys(selectedFilters).some((f) => selectedFilters[f])) {
				if (selectedFilters.standard_kennel) {
					filteredData = filteredData.filter(
						(d) =>
							d.visit &&
							!d.visit.sharing_kennels &&
							(!d.boarding_option ||
								(d.boarding_option && d.boarding_option.kennel_type === 'Standard'))
					)
				}

				if (selectedFilters.luxury_kennel) {
					filteredData = filteredData.filter(
						(d) =>
							d.visit &&
							!d.visit.sharing_kennels &&
							d.boarding_option &&
							d.boarding_option.kennel_type === 'Luxury'
					)
				}

				if (selectedFilters['standard-shared']) {
					filteredData = filteredData.filter(
						(d) =>
							d.visit &&
							d.visit.sharing_kennels &&
							(!d.boarding_option ||
								(d.boarding_option && d.boarding_option.kennel_type === 'Standard'))
					)
				}

				if (selectedFilters['luxury-shared']) {
					filteredData = filteredData.filter(
						(d) =>
							d.visit &&
							d.visit.sharing_kennels &&
							d.boarding_option &&
							d.boarding_option.kennel_type === 'Luxury'
					)
				}

				if (selectedFilters.kennel_free) {
					filteredData = filteredData.filter(
						(d) =>
							d.visit &&
							!d.visit.sharing_kennels &&
							d.boarding_option &&
							d.boarding_option.kennel_type === 'Kennel-Free'
					)
				}

				if (selectedFilters.kennel_assigned) {
					filteredData = filteredData.filter((d) =>
						facility.kennels.some((k) =>
							Object.keys(k.pet_ids).some((t) => k.pet_ids[t].includes(d._id))
						)
					)
				}

				if (selectedFilters.kennel_unassigned) {
					filteredData = filteredData.filter((d) =>
						facility.kennels.every((k) =>
							Object.keys(k.pet_ids).every((t) => !k.pet_ids[t].includes(d._id))
						)
					)
				}
			}

			setData(filteredData)
		}
	}, [pets, selectedFilters])

	const changeDate = (type) => {
		if (type === 'add') {
			setDate(moment(date).add(1, 'd').format('YYYY-MM-DD'))
		} else if (type === 'subtract') {
			setDate(moment(date).subtract(1, 'd').format('YYYY-MM-DD'))
		}
	}

	useEffect(() => {
		let updatedAggregates = cloneDeep(aggregates)

		if (data.length > 0) {
			updatedAggregates.boarding_dogs_tonight.value = data.length
			updatedAggregates.standard_kennels.value = data.filter(
				(d) =>
					!d.visit.sharing_kennels &&
					(!d.boarding_option || (d.boarding_option && d.boarding_option.kennel_type === 'Standard'))
			).length
			updatedAggregates.luxury_kennels.value = data.filter(
				(d) => !d.visit.sharing_kennels && d.boarding_option && d.boarding_option.kennel_type === 'Luxury'
			).length
			updatedAggregates['standard-shared_kennels'].value = data.filter(
				(d) =>
					(!d.boarding_option || (d.boarding_option && d.boarding_option.kennel_type === 'Standard')) &&
					d.visit &&
					d.visit.sharing_kennels
			).length
			updatedAggregates['luxury-shared_kennels'].value = data.filter(
				(d) =>
					d.boarding_option &&
					d.boarding_option.kennel_type === 'Luxury' &&
					d.visit &&
					d.visit.sharing_kennels
			).length
			updatedAggregates.kennel_free_dogs.value = data.filter(
				(d) => d.boarding_option && d.boarding_option.kennel_type === 'Kennel-Free'
			).length

			setAggregates(updatedAggregates)
		} else {
			for (const key in updatedAggregates) {
				if (updatedAggregates.hasOwnProperty(key)) {
					if (typeof updatedAggregates[key] === 'object') {
						updatedAggregates[key].value = 0
					}
				}
			}

			setAggregates(updatedAggregates)
		}
	}, [data])

	return (
		<div className={classes.root}>
			{facility ? (
				<div className={classes.visitsTableContainer}>
					<KennelsTable
						aggregates={aggregates}
						data={data}
						columns={getColumns()}
						date={date}
						changeDate={changeDate}
						setDate={setDate}
					/>
					<KennelBlockDialogNew
						isShown={isKennelBlockPopupShown}
						setIsShown={setIsKennelBlockPopupShown}
						facility={facility}
						pet={pet}
						time="night"
						assignKennel={(kennel) => {
							dispatch(
								updateKennelAssignment(facility._id, kennel._id, {
									pet_id: pet._id,
									kennel_time: 'day'
								})
							)
							setPet(null)
							setIsKennelBlockPopupShown(false)
						}}
					/>
				</div>
			) : (
				<h3>Please Select a Facility</h3>
			)}
		</div>
	)
}

export default Kennels
